import type { Session } from "@supabase/supabase-js";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { demoProfiles } from "../data/demo";
import i18n from "../lib/i18n";
import { isDemoMode, isGoogleAuthEnabled, supabase } from "../lib/supabase";
import type { WorkspaceProfile } from "../types/domain";

interface AuthValue {
  session: Session | null;
  profile: WorkspaceProfile | null;
  loading: boolean;
  demoMode: boolean;
  signIn(email: string, password: string): Promise<string | null>;
  signInWithGoogle(): Promise<string | null>;
  signOut(): Promise<void>;
  updatePassword(currentPassword: string, password: string): Promise<string | null>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<WorkspaceProfile | null>(isDemoMode ? demoProfiles[0] : null);
  const [loading, setLoading] = useState(!isDemoMode);

  useEffect(() => {
    if (!supabase) return;
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setProfile(profileFromSession(data.session));
      setLoading(false);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setProfile(profileFromSession(nextSession));
      setLoading(false);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  const value = useMemo<AuthValue>(() => ({
    session,
    profile,
    loading,
    demoMode: isDemoMode,
    async signIn(email, password) {
      if (!supabase) {
        const match = demoProfiles.find((candidate) => candidate.email.toLowerCase() === email.toLowerCase());
        setProfile(match ?? demoProfiles[0]);
        return null;
      }
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return error?.message ?? null;
    },
    async signInWithGoogle() {
      if (!supabase) return null;
      if (!isGoogleAuthEnabled) return i18n.t("login.googleUnavailable");
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: `${window.location.origin}/workspace` },
      });
      return error?.message ?? null;
    },
    async signOut() {
      if (supabase) await supabase.auth.signOut();
    },
    async updatePassword(currentPassword, password) {
      if (!supabase) return null;
      if (!session?.user.email) return i18n.t("account.noSession");
      const { error: verificationError } = await supabase.auth.signInWithPassword({ email: session.user.email, password: currentPassword });
      if (verificationError) return i18n.t("account.wrongCurrentPassword");
      const { error } = await supabase.auth.updateUser({ password });
      return error?.message ?? null;
    },
  }), [loading, profile, session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function profileFromSession(session: Session | null): WorkspaceProfile | null {
  if (!session?.user.email) return null;
  const email = session.user.email;
  const metadataName = typeof session.user.user_metadata.full_name === "string" ? session.user.user_metadata.full_name.trim() : "";
  // The known list may only supply display metadata. The id must stay the auth
  // UUID: card_assignees.user_id and save_legal_response_version both key on it.
  const known = demoProfiles.find((item) => item.email.toLowerCase() === email.toLowerCase());
  const displayName = known?.displayName || metadataName || email.split("@")[0];
  return {
    id: session.user.id,
    email,
    displayName,
    initials: known?.initials ?? initialsFor(displayName),
    role: "admin",
  };
}

function initialsFor(name: string) {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length > 1) return parts.map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
